// src/pages/Terms.jsx
import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaChevronDown,
  FaCopy,
  FaCheck,
  FaFileContract,
} from "react-icons/fa";

const CLAUSES = [
  {
    id: 1,
    title: "Pricing may vary",
    body:
      "Prices may vary based on final requirements, third-party costs, or integrations. Final scope and price are confirmed after requirements and proposals are agreed.",
  },
  {
    id: 2,
    title: "Milestone-based payments",
    body:
      "All payments are milestone-based. A deposit is required to begin work and subsequent payments are tied to agreed deliverables and milestones.",
  },
  {
    id: 3,
    title: "Free support period",
    body:
      "Most projects include 3 months of complimentary support after delivery. Support covers bug fixes and minor adjustments. New features are scoped separately.",
  },
  {
    id: 4,
    title: "Extra revisions",
    body:
      "Extra revisions beyond the agreed scope are billed separately. We recommend consolidated feedback rounds per milestone to keep timelines predictable.",
  },
  {
    id: 5,
    title: "Client responsibilities",
    body:
      "Client should provide required content, access and approvals in a timely manner. Delays in inputs may impact delivery dates and lead to additional costs.",
  },
  {
    id: 6,
    title: "Jurisdiction",
    body:
      "All legal disputes will be governed by the laws and courts of India. IP transfer and ownership terms are specified in the contract/SOW.",
  },
];

export default function Terms() {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(1);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    AOS.init({
      duration: 650,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);

  const filteredClauses = CLAUSES.filter((clause) => {
    const search = query.trim().toLowerCase();

    if (!search) return true;

    return (
      clause.title.toLowerCase().includes(search) ||
      clause.body.toLowerCase().includes(search)
    );
  });

  const toggleClause = (id) => {
    setOpenId((current) => (current === id ? null : id));
  };

  const expandAll = () => {
    setOpenId("all");
  };

  const collapseAll = () => {
    setOpenId(null);
  };

  const resetSearch = () => {
    setQuery("");
    setOpenId(1);
  };

  const copyClause = async (clause) => {
    try {
      await navigator.clipboard.writeText(
        `${clause.title}\n\n${clause.body}`
      );

      setCopiedId(clause.id);

      setTimeout(() => {
        setCopiedId(null);
      }, 1500);
    } catch (error) {
      console.error("Unable to copy clause:", error);
    }
  };

  return (
    <main className="min-h-screen bg-[#f8fafc] px-4 pb-24 pt-28 text-[#0b1220] sm:px-6">

      <div className="mx-auto max-w-4xl">

        {/* Header */}
        <header
          className="mb-8 text-center"
          data-aos="fade-down"
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-50 text-cyan-500">
            <FaFileContract />
          </div>

          <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl md:text-5xl">
            Terms & Conditions
          </h1>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
            Simple and transparent terms covering our services, payments,
            support and project responsibilities.
          </p>
        </header>

        {/* Search + Controls */}
        <div
          className="mb-6 space-y-3"
          data-aos="fade-up"
        >
          <div className="flex flex-col gap-3 sm:flex-row">

            <div className="relative flex-1">
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search terms, pricing, payments..."
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#0b1220] shadow-sm outline-none transition placeholder:text-slate-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/10"
                aria-label="Search terms and conditions"
              />

              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-slate-700"
                  aria-label="Clear search"
                >
                  Clear
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={resetSearch}
              className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Reset
            </button>

          </div>

          <div className="flex flex-wrap gap-2">

            <button
              type="button"
              onClick={expandAll}
              className="rounded-lg bg-gradient-to-r from-[#06b6d4] to-[#3b82f6] px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              Expand All
            </button>

            <button
              type="button"
              onClick={collapseAll}
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              Collapse
            </button>

            <span className="ml-auto self-center text-xs text-slate-500">
              {filteredClauses.length}{" "}
              {filteredClauses.length === 1 ? "clause" : "clauses"}
            </span>

          </div>
        </div>

        {/* Clauses */}
        <div className="space-y-3">

          {filteredClauses.length === 0 && (
            <div
              className="rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm"
              data-aos="fade-up"
            >
              <p className="text-sm font-semibold text-slate-700">
                No terms found
              </p>

              <p className="mt-1 text-xs text-slate-500">
                Try searching for another keyword.
              </p>

              <button
                type="button"
                onClick={resetSearch}
                className="mt-4 text-sm font-semibold text-cyan-500 hover:text-cyan-600"
              >
                Clear search
              </button>
            </div>
          )}

          {filteredClauses.map((clause, index) => {
            const isOpen =
              openId === "all" || openId === clause.id;

            return (
              <article
                key={clause.id}
                data-aos="fade-up"
                data-aos-delay={index * 50}
                className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-md"
              >

                {/* Clause Header */}
                <div className="flex items-center gap-3 px-4 py-4 sm:px-5">

                  <button
                    type="button"
                    onClick={() => toggleClause(clause.id)}
                    className="flex min-w-0 flex-1 items-center gap-4 text-left"
                    aria-expanded={isOpen}
                    aria-controls={`clause-${clause.id}`}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-xs font-bold text-slate-400">
                      {String(clause.id).padStart(2, "0")}
                    </span>

                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-[#0b1220] sm:text-base">
                        {clause.title}
                      </span>

                      <span className="mt-0.5 block text-xs text-slate-500">
                        Clause #{clause.id}
                      </span>
                    </span>
                  </button>

                  {/* Copy */}
                  <button
                    type="button"
                    onClick={() => copyClause(clause)}
                    title="Copy clause"
                    aria-label={`Copy clause ${clause.id}`}
                    className="hidden h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-cyan-500 sm:flex"
                  >
                    {copiedId === clause.id ? (
                      <FaCheck className="text-emerald-500" />
                    ) : (
                      <FaCopy />
                    )}
                  </button>

                  {/* Toggle */}
                  <button
                    type="button"
                    onClick={() => toggleClause(clause.id)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-cyan-500"
                    aria-label={
                      isOpen
                        ? `Hide clause ${clause.id}`
                        : `Read clause ${clause.id}`
                    }
                  >
                    <FaChevronDown
                      className={`text-xs transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                </div>

                {/* Clause Body */}
                <div
                  id={`clause-${clause.id}`}
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="min-h-0 overflow-hidden">

                    <div className="border-t border-slate-100 px-5 pb-5 pt-4 sm:pl-[4.75rem]">

                      <p className="text-sm leading-7 text-slate-700">
                        {clause.body}
                      </p>

                      {/* Mobile Copy */}
                      <button
                        type="button"
                        onClick={() => copyClause(clause)}
                        className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-cyan-500 sm:hidden"
                      >
                        {copiedId === clause.id ? (
                          <>
                            <FaCheck />
                            Copied
                          </>
                        ) : (
                          <>
                            <FaCopy />
                            Copy Clause
                          </>
                        )}
                      </button>

                    </div>

                  </div>
                </div>

              </article>
            );
          })}

        </div>

        {/* Bottom CTA */}
        <section
          data-aos="fade-up"
          className="mt-8 rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm sm:p-9"
        >
          <p className="text-xs font-bold tracking-[0.18em] text-cyan-500">
            NEED CLARIFICATION?
          </p>

          <h2 className="mt-3 text-2xl font-bold sm:text-3xl">
            Have Questions About Our Terms?
          </h2>

          <p className="mx-auto mt-3 max-w-xl text-sm leading-7 text-slate-600">
            If you have questions about pricing, support, payments or project
            terms, our team will be happy to discuss them with you.
          </p>

          <a
            href="/contact"
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-[#06b6d4] to-[#3b82f6] px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            Contact Us
          </a>
        </section>

      </div>
    </main>
  );
}